export type UploadResult = {
  storageKey: string;
  durationSeconds: number;
};

export type QuickAddRecordingResult = {
  id: string;
  storageKey: string;
  durationSeconds: number;
  position: number;
  createdAt: string;
  signedAudioUrl: string;
  filename: string;
};

/**
 * Extract duration from an audio file using the Web Audio API.
 */
async function extractDuration(file: File): Promise<number> {
  const arrayBuffer = await file.arrayBuffer();
  const audioContext = new AudioContext();
  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    return audioBuffer.duration;
  } finally {
    await audioContext.close();
  }
}

/**
 * Upload a file using a presigned URL.
 * Returns the storage key and duration on success, throws on failure.
 */
export async function uploadFile(
  file: File,
  getUrl: (
    name: string,
    type: string,
    size: number,
  ) => Promise<{ uploadUrl: string; storageKey: string }>,
): Promise<UploadResult> {
  // Extract duration and get upload URL in parallel
  const [durationSeconds, { uploadUrl, storageKey }] = await Promise.all([
    extractDuration(file),
    getUrl(file.name, file.type, file.size),
  ]);

  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }

  return { storageKey, durationSeconds };
}

/**
 * Upload a recording directly to a song (quick-add flow).
 * Uploads to S3 and creates the recording record in one operation.
 */
type UploadUrlGetter = (
  name: string,
  type: string,
  size: number,
) => Promise<{ uploadUrl: string; storageKey: string }>;

export async function uploadRecordingToSong(
  file: File,
  songId: string,
  getUploadUrl: UploadUrlGetter,
  addRecording: (
    songId: string,
    recording: { storageKey: string; durationSeconds: number },
  ) => Promise<QuickAddRecordingResult>,
): Promise<QuickAddRecordingResult> {
  // First upload to S3
  const { storageKey, durationSeconds } = await uploadFile(file, getUploadUrl);

  // Then create the recording record
  return addRecording(songId, { storageKey, durationSeconds });
}
